import React, { useEffect, useState, useRef } from "react";
import * as pdfjsLib from "pdfjs-dist";
import { Row, Col, Card } from "react-bootstrap";
import { Btn } from "../../../../shared/Btn";
import {
  getTemplateExtractedData,
  getTemplate,
  getTemplateExtractedDataTagLevel,
} from "src/docuclipper/api";
import {
  Rectangle,
  convertToRectangles,
  drawRectangles,
  toDisplayCoordinates,
  toBackendCoordinates,
} from "./pdfHelpers";
import { useLocation } from "react-router-dom";
import queryString from "query-string";
import { CsvToHtmlTable } from "../../../../react-csv-to-table";
import { parseCsvToRowsAndColumn } from "../../../../react-csv-to-table/utils";
import useBankStatementPDF from "../../DataArchive/TransactionManager/useBankStatementPDF";
import { useSelector } from "react-redux";
import { ReduxState } from "src/redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { CopyToClipboard } from "react-copy-to-clipboard";
import { useL1L2Params } from "src/hooks/useL1L2Params";

// Set up PDF.js worker
pdfjsLib.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.js`;

// Add backend dimensions constants
const BACKEND_WIDTH = 2550;
const BACKEND_HEIGHT = 3300;

type Props = {
  imageSrc: string;
  documentId;
  jobId;
};

type ExtractedData = {
  documentId: string;
  documentPath: string;
  originalname: string;
  output: Array<{
    toolType?: string;
    fieldType?: string;
    tableMapping: any[];
    tableConfig: {
      columns: any[];
      headerRows: number;
      transforms: {
        verticalMergeMasks: null;
        removeTopRows: null;
        removeBottomRows: null;
      };
    };
    name: string;
    output: string;
    hasHeader: boolean;
    rectangleId: string;
    pageNumber: string;
    x0: number;
    x1: number;
    y0: number;
    y1: number;
    templateRectangle: any;
    acc: number;
  }>;
  pageNumber: number;
  user: string;
};

const ExtractedDataViewer: React.FC<Props> = ({
  imageSrc,
  documentId,
  jobId,
}) => {
  const location = useLocation();
  const query = queryString.parse(location.search);
  const templateId = query.templateId
    ? parseInt(query.templateId as string, 10)
    : null;

  // console.log("ExtractedDataViewer mounted with imageSrc:", imageSrc);

  const [extractedData, setExtractedData] = useState<ExtractedData | null>(
    null
  );
  const [rectangles, setRectangles] = useState<Rectangle[]>([]);
  const [selectedRectangle, setSelectedRectangle] = useState<Rectangle | null>(
    null
  );
  const [pdfDoc, setPdfDoc] = useState<pdfjsLib.PDFDocumentProxy | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [scale, setScale] = useState<number>(1);
  const [pageThumbnails, setPageThumbnails] = useState<
    Array<{ pageNumber: number; thumbnail: string }>
  >([]);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [viewport, setViewport] = useState<pdfjsLib.PageViewport | null>(null);
  const [tableRows, setTableRows] = useState<string[][]>([]);

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const rectCanvasRef = useRef<HTMLCanvasElement>(null);
  const renderTaskRef = useRef<pdfjsLib.RenderTask | null>(null);

  // Separate effect for PDF loading
  useEffect(() => {
    // console.log("PDF loading effect triggered with imageSrc:", imageSrc);
    const loadPdf = async () => {
      try {
        // console.log("Starting PDF load from:", imageSrc);
        const loadingTask = pdfjsLib.getDocument(imageSrc);
        // console.log("Loading task created");
        const pdf = await loadingTask.promise;
        // console.log("PDF loaded successfully:", pdf);
        setPdfDoc(pdf);
        setTotalPages(pdf.numPages);

        // Generate thumbnails
        const thumbnails: Array<{ pageNumber: number; thumbnail: string }> = [];
        for (let i = 1; i <= pdf.numPages; i++) {
          const page = await pdf.getPage(i);
          const viewport = page.getViewport({ scale: 0.2 });
          const canvas = document.createElement("canvas");
          const context = canvas.getContext("2d");
          canvas.width = viewport.width;
          canvas.height = viewport.height;

          await page.render({
            canvasContext: context!,
            viewport: viewport,
          }).promise;

          thumbnails.push({
            pageNumber: i,
            thumbnail: canvas.toDataURL(),
          });
        }
        setPageThumbnails(thumbnails);
      } catch (error) {
        console.error("Error loading PDF:", error);
      }
    };

    loadPdf();
  }, [imageSrc]);

  // Add effect to clear canvas when page changes
  useEffect(() => {
    if (rectCanvasRef.current) {
      const context = rectCanvasRef.current.getContext("2d");
      if (context) {
        context.clearRect(
          0,
          0,
          rectCanvasRef.current.width,
          rectCanvasRef.current.height
        );
      }
    }
  }, [currentPage]);

  const { l1, l2 } = useL1L2Params();
  // Separate effect for data fetching
  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      if (!viewport) {
        // console.log("Waiting for viewport to be initialized...");
        return;
      }

      // Clear existing rectangles at the start of fetch
      setRectangles([]);
      setSelectedRectangle(null);

      try {
        // console.log("Fetching data with viewport:", viewport);
        let fn = jobId
          ? getTemplateExtractedData(jobId, documentId, currentPage - 1)
          : getTemplateExtractedDataTagLevel(
              l1,
              l2,
              documentId,
              currentPage - 1
            );
        const response = await fn;

        if (!response?.results?.[documentId]) {
          // console.log("No data found for document:", documentId);
          return;
        }

        const data = response.results[documentId];
        // console.log("Fetched data:", data);

        if (!isMounted) return;
        setExtractedData(data);

        // Convert extracted data to rectangles using the shared helper
        const newRectangles = convertToRectangles(data.output, viewport);
        // console.log("Created rectangles:", newRectangles);

        if (!isMounted) return;
        setRectangles(newRectangles);
        // console.log({ newRectangles });

        // Select the first rectangle if available and set the current page
        if (newRectangles.length > 0) {
          // console.log("Setting selected rectangle:", newRectangles[0]);
          setSelectedRectangle(newRectangles[0]);
          // console.log("Setting current page to:", newRectangles[0].pageNumber);
          setCurrentPage(newRectangles[0].pageNumber);
        } else {
          // console.log("No rectangles found in the data");
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        // Clear rectangles on error as well
        setRectangles([]);
        setSelectedRectangle(null);
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, [viewport, currentPage]); // Add viewport and currentPage as dependencies

  // Add effect to log rectangle changes
  useEffect(() => {
    // console.log("Rectangles updated:", rectangles);
  }, [rectangles]);

  // Add effect to log selected rectangle changes
  useEffect(() => {
    // console.log("Selected rectangle updated:", selectedRectangle);
  }, [selectedRectangle]);

  // Add effect to log current page changes
  useEffect(() => {
    // console.log("Current page updated:", currentPage);
  }, [currentPage]);

  // Add effect to log viewport changes
  useEffect(() => {
    // console.log("Viewport updated:", viewport);
  }, [viewport]);

  // Separate effect for template loading
  useEffect(() => {
    const loadTemplate = async () => {
      if (!templateId) return;

      try {
        const template = await getTemplate(templateId);
        const templateObj = JSON.parse(template.template);

        // Convert template rectangles to our format
        const newRectangles = templateObj.rectangles.map((rect: any) => ({
          id: rect.id,
          x: rect.x,
          y: rect.y,
          width: rect.width,
          height: rect.height,
          fieldType: rect.fieldType,
          pageNumber: rect.pageNumber,
          extractedData: rect.extractedData,
        }));

        setRectangles(newRectangles);

        // Select the first rectangle if available
        if (newRectangles.length > 0) {
          setSelectedRectangle(newRectangles[0]);
        }
      } catch (error) {
        console.error("Error loading template:", error);
      }
    };

    loadTemplate();
  }, [templateId]);

  // Add effect to parse table data when selected rectangle changes
  useEffect(() => {
    if (!selectedRectangle) {
      setTableRows([]);
      return;
    }
    if (selectedRectangle.fieldType === "table") {
      const csvData = selectedRectangle.extractedData?.output;
      if (csvData != null && typeof csvData === "string") {
        setTableRows(parseCsvToRowsAndColumn(csvData));
      } else {
        setTableRows([]);
      }
    } else {
      setTableRows([]);
    }
  }, [selectedRectangle]);

  const renderPage = async () => {
    if (!pdfDoc || !canvasRef.current) {
      // console.log("Cannot render: pdfDoc or canvas not ready", {
      //   pdfDoc: !!pdfDoc,
      //   canvas: !!canvasRef.current,
      // });
      return;
    }

    try {
      // Cancel any ongoing render task
      if (renderTaskRef.current) {
        renderTaskRef.current.cancel();
        renderTaskRef.current = null;
      }

      // console.log("Getting page:", currentPage);
      const page = await pdfDoc.getPage(currentPage);
      // console.log("Got page, creating viewport");
      const newViewport = page.getViewport({ scale });
      setViewport(newViewport);

      const canvas = canvasRef.current;
      const context = canvas.getContext("2d");

      if (!context) {
        console.error("Could not get canvas context");
        return;
      }

      // console.log("Setting canvas dimensions:", {
      //   width: newViewport.width,
      //   height: newViewport.height,
      // });
      canvas.height = newViewport.height;
      canvas.width = newViewport.width;

      // Set the same dimensions for the rectangle canvas
      if (rectCanvasRef.current) {
        rectCanvasRef.current.height = newViewport.height;
        rectCanvasRef.current.width = newViewport.width;
        // Clear the rectangle canvas
        const rectContext = rectCanvasRef.current.getContext("2d");
        if (rectContext) {
          rectContext.clearRect(0, 0, newViewport.width, newViewport.height);
        }
      }

      const renderContext = {
        canvasContext: context,
        viewport: newViewport,
      };

      // console.log("Starting page render");
      renderTaskRef.current = page.render(renderContext);
      await renderTaskRef.current.promise;
      // console.log("Page rendered successfully");

      // Draw existing rectangles using the shared helper
      if (rectCanvasRef.current) {
        const rectContext = rectCanvasRef.current.getContext("2d");
        if (rectContext) {
          drawRectangles(rectContext, rectangles, selectedRectangle, scale);
        }
      }
    } catch (error) {
      if (error instanceof Error && error.message.includes("cancelled")) {
        // console.log("Render was cancelled");
      } else {
        console.error("Error rendering page:", error);
      }
    }
  };

  // Render PDF page when dependencies change
  useEffect(() => {
    renderPage();

    // Cleanup function to cancel any ongoing render task
    return () => {
      if (renderTaskRef.current) {
        renderTaskRef.current.cancel();
        renderTaskRef.current = null;
      }
    };
  }, [pdfDoc, currentPage, scale]);

  // Redraw rectangles when they change
  useEffect(() => {
    if (rectCanvasRef.current) {
      const context = rectCanvasRef.current.getContext("2d");
      if (context) {
        // Clear the canvas first
        context.clearRect(
          0,
          0,
          rectCanvasRef.current.width,
          rectCanvasRef.current.height
        );

        // Only draw rectangles if we have any for the current page
        const visibleRectangles = rectangles.filter(
          (r) => r.pageNumber === currentPage
        );
        if (visibleRectangles.length > 0) {
          // console.log(
          //   "Drawing rectangles for page",
          //   currentPage,
          //   ":",
          //   visibleRectangles
          // );
          drawRectangles(context, visibleRectangles, selectedRectangle, scale);
        }
      }
    }
  }, [rectangles, selectedRectangle, scale, currentPage]);

  const handlePageChange = (newPage: number) => {
    // Clear rectangles state first
    setRectangles([]);
    setSelectedRectangle(null);

    // Clear the canvas
    if (rectCanvasRef.current) {
      const context = rectCanvasRef.current.getContext("2d");
      if (context) {
        context.clearRect(
          0,
          0,
          rectCanvasRef.current.width,
          rectCanvasRef.current.height
        );
      }
    }

    // Then change the page
    setCurrentPage(newPage);
  };

  const handleRectangleClick = (rectangle: Rectangle) => {
    // console.log("Rectangle clicked:", rectangle);
    setSelectedRectangle(rectangle);
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "20px",
        padding: "20px",
        position: "relative",
        height: "85vh",
        width: "100%",
      }}
    >
      {/* Controls Bar */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "10px",
          padding: "10px",
          background: "#f5f5f5",
          borderRadius: "8px",
          boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
        }}
      >
        <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
          <button
            onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
            style={{
              padding: "6px 12px",
              background: "#fff",
              border: "1px solid #ddd",
              borderRadius: "4px",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "4px",
              fontSize: "13px",
            }}
          >
            <span>←</span> Previous
          </button>
          <span style={{ padding: "0 10px", fontSize: "13px" }}>
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={() =>
              handlePageChange(Math.min(totalPages, currentPage + 1))
            }
            style={{
              padding: "6px 12px",
              background: "#fff",
              border: "1px solid #ddd",
              borderRadius: "4px",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "4px",
              fontSize: "13px",
            }}
          >
            Next <span>→</span>
          </button>
        </div>
      </div>

      <div style={{ display: "flex", gap: "20px", flex: 1, minHeight: 0 }}>
        {/* Page thumbnails sidebar */}
        <div
          style={{
            width: "120px",
            padding: "10px",
            background: "#f5f5f5",
            borderRadius: "8px",
            display: "flex",
            flexDirection: "column",
            gap: "10px",
            overflowY: "auto",
            maxHeight: "100%",
            flexShrink: 0,
          }}
        >
          {pageThumbnails.map(({ pageNumber, thumbnail }) => (
            <div
              key={pageNumber}
              style={{
                cursor: "pointer",
                border:
                  pageNumber === currentPage
                    ? "2px solid #FF6B6B"
                    : "1px solid #ccc",
                borderRadius: "4px",
                padding: "4px",
                background: "white",
              }}
              onClick={() => handlePageChange(pageNumber)}
            >
              <img
                src={thumbnail}
                alt={`Page ${pageNumber}`}
                style={{ width: "100%", height: "auto", display: "block" }}
              />
              <div
                style={{
                  textAlign: "center",
                  fontSize: "12px",
                  marginTop: "4px",
                }}
              >
                Page {pageNumber}
              </div>
            </div>
          ))}
        </div>

        <div
          style={{
            flex: "1 1 auto",
            position: "relative",
            minWidth: 0,
            height: "100%",
          }}
        >
          <div style={{ position: "relative", height: "100%" }}>
            <canvas
              ref={canvasRef}
              style={{
                display: "block",
                margin: "0 auto",
                border: "1px solid #ccc",
                background: "#fafafa",
                position: "absolute",
                top: 0,
                left: 0,
                zIndex: 1,
                height: "100%",
              }}
            />
            <canvas
              ref={rectCanvasRef}
              style={{
                display: "block",
                margin: "0 auto",
                position: "absolute",
                top: 0,
                left: 0,
                cursor: "crosshair",
                zIndex: 2,
                height: "100%",
              }}
              onMouseMove={(e) => {
                const rect = rectCanvasRef.current?.getBoundingClientRect();
                if (!rect) return;

                // Calculate mouse position relative to canvas, accounting for canvas position and size
                const mouseX =
                  (e.clientX - rect.left) *
                  (rect.width / rectCanvasRef.current!.width);
                const mouseY =
                  (e.clientY - rect.top) *
                  (rect.height / rectCanvasRef.current!.height);

                // console.log("Mouse move:", {
                //   clientX: e.clientX,
                //   clientY: e.clientY,
                //   rectLeft: rect.left,
                //   rectTop: rect.top,
                //   mouseX,
                //   mouseY,
                //   scale,
                //   canvasWidth: rect.width,
                //   canvasHeight: rect.height,
                //   canvasActualWidth: rectCanvasRef.current!.width,
                //   canvasActualHeight: rectCanvasRef.current!.height,
                // });

                // Find if we're over any rectangle
                const isOverRectangle = rectangles.some((r) => {
                  const rectX = r.x * scale;
                  const rectY = r.y * scale;
                  const rectWidth = r.width * scale;
                  const rectHeight = r.height * scale;

                  const isInside =
                    mouseX >= rectX &&
                    mouseX <= rectX + rectWidth &&
                    mouseY >= rectY &&
                    mouseY <= rectY + rectHeight;

                  // console.log("Checking rectangle in mouse move:", {
                  //   rect: r,
                  //   isInside,
                  //   mouseX,
                  //   mouseY,
                  //   rectX,
                  //   rectY,
                  //   rectWidth,
                  //   rectHeight,
                  //   scale,
                  // });

                  return isInside;
                });

                // Change cursor style based on whether we're over a rectangle
                if (rectCanvasRef.current) {
                  rectCanvasRef.current.style.cursor = isOverRectangle
                    ? "pointer"
                    : "default";
                }
              }}
              onClick={(e) => {
                const rect = rectCanvasRef.current?.getBoundingClientRect();
                if (!rect) return;

                // Calculate click position relative to canvas, accounting for canvas position and size
                const clickX =
                  (e.clientX - rect.left) *
                  (rect.width / rectCanvasRef.current!.width);
                const clickY =
                  (e.clientY - rect.top) *
                  (rect.height / rectCanvasRef.current!.height);

                // console.log("Click coordinates:", {
                //   clientX: e.clientX,
                //   clientY: e.clientY,
                //   rectLeft: rect.left,
                //   rectTop: rect.top,
                //   clickX,
                //   clickY,
                //   scale,
                //   canvasWidth: rect.width,
                //   canvasHeight: rect.height,
                //   canvasActualWidth: rectCanvasRef.current!.width,
                //   canvasActualHeight: rectCanvasRef.current!.height,
                // });

                // Find the clicked rectangle
                const clickedRectangle = rectangles.find((r) => {
                  // Get rectangle bounds in canvas coordinates (matching how we draw them)
                  const rectX = r.x * scale;
                  const rectY = r.y * scale;
                  const rectWidth = r.width * scale;
                  const rectHeight = r.height * scale;

                  const isInside =
                    clickX >= rectX &&
                    clickX <= rectX + rectWidth &&
                    clickY >= rectY &&
                    clickY <= rectY + rectHeight;

                  // console.log("Checking rectangle in click:", {
                  //   rect: r,
                  //   isInside,
                  //   clickX,
                  //   clickY,
                  //   rectX,
                  //   rectY,
                  //   rectWidth,
                  //   rectHeight,
                  //   scale,
                  // });

                  return isInside;
                });

                if (clickedRectangle) {
                  // console.log("Rectangle clicked:", clickedRectangle);
                  setSelectedRectangle(clickedRectangle);
                } else {
                  // console.log("No rectangle clicked");
                }
              }}
            />
          </div>
        </div>

        {/* Extracted Data Sidebar */}
        <div
          style={{
            width: "300px",
            padding: "20px",
            background: "#f5f5f5",
            borderRadius: "8px",
            display: "flex",
            flexDirection: "column",
            gap: "20px",
            height: "100%",
            overflowY: "auto",
            flexShrink: 0,
          }}
        >
          {/* Rectangle Selection Dropdown */}
          <select
            value={
              selectedRectangle
                ? `${selectedRectangle.pageNumber}-${selectedRectangle.x}-${selectedRectangle.y}`
                : ""
            }
            onChange={(e) => {
              const [pageNumber, x, y] = e.target.value.split("-").map(Number);
              const selected = rectangles.find(
                (r) => r.pageNumber === pageNumber && r.x === x && r.y === y
              );
              if (selected) {
                setSelectedRectangle(selected);
              }
            }}
            style={{
              padding: "8px 12px",
              background: "#fff",
              border: "1px solid #ddd",
              borderRadius: "4px",
              cursor: "pointer",
              fontSize: "14px",
              width: "100%",
              marginBottom: "10px",
            }}
          >
            <option value="">Select a rectangle</option>
            {rectangles.map((rectangle) => (
              <option
                key={`${rectangle.pageNumber}-${rectangle.x}-${rectangle.y}`}
                value={`${rectangle.pageNumber}-${rectangle.x}-${rectangle.y}`}
              >
                {rectangle.extractedData.name}
              </option>
            ))}
          </select>

          {selectedRectangle ? (
            <div
              style={{
                padding: "15px",
                background: "white",
                borderRadius: "4px",
                border: "1px solid #ddd",
              }}
            >
              <h4 style={{ margin: "0 0 15px 0" }}>
                {selectedRectangle.extractedData.name}
              </h4>
              {selectedRectangle.fieldType === "text" ? (
                <div>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "flex-end",
                      gap: "10px",
                      marginBottom: "10px",
                    }}
                  >
                    <CopyToClipboard
                      text={selectedRectangle.extractedData.output}
                    >
                      <Btn variant="tertiary" size="sm">
                        <FontAwesomeIcon icon="copy" className="mr-1" />
                        Copy
                      </Btn>
                    </CopyToClipboard>
                    <Btn
                      variant="tertiary"
                      size="sm"
                      onClick={() => {
                        const blob = new Blob(
                          [selectedRectangle.extractedData.output],
                          { type: "text/plain" }
                        );
                        const url = window.URL.createObjectURL(blob);
                        const a = document.createElement("a");
                        a.href = url;
                        a.download = `${selectedRectangle.extractedData.name}.txt`;
                        document.body.appendChild(a);
                        a.click();
                        window.URL.revokeObjectURL(url);
                        document.body.removeChild(a);
                      }}
                    >
                      <FontAwesomeIcon icon="download" className="mr-1" />
                      Download
                    </Btn>
                  </div>
                  <div>{selectedRectangle.extractedData.output}</div>
                </div>
              ) : (
                <div style={{ maxHeight: "400px" }}>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "flex-end",
                      gap: "10px",
                      marginBottom: "10px",
                    }}
                  >
                    <CopyToClipboard
                      text={tableRows.map((row) => row.join(",")).join("\n")}
                    >
                      <Btn variant="tertiary" size="sm">
                        <FontAwesomeIcon icon="copy" className="mr-1" />
                        Copy CSV
                      </Btn>
                    </CopyToClipboard>
                    <Btn
                      variant="tertiary"
                      size="sm"
                      onClick={() => {
                        const csvContent = tableRows
                          .map((row) => row.join(","))
                          .join("\n");
                        const blob = new Blob([csvContent], {
                          type: "text/csv",
                        });
                        const url = window.URL.createObjectURL(blob);
                        const a = document.createElement("a");
                        a.href = url;
                        a.download = `${selectedRectangle.extractedData.name}.csv`;
                        document.body.appendChild(a);
                        a.click();
                        window.URL.revokeObjectURL(url);
                        document.body.removeChild(a);
                      }}
                    >
                      <FontAwesomeIcon icon="download" className="mr-1" />
                      Download CSV
                    </Btn>
                  </div>
                  <CsvToHtmlTable
                    rows={tableRows}
                    csvDelimiter=","
                    tableClassName="table table-striped table-hover"
                    tableRowClassName=""
                    tableColumnClassName=""
                    hasHeader={selectedRectangle.extractedData.hasHeader}
                  />
                </div>
              )}
            </div>
          ) : (
            <div style={{ color: "#666" }}>
              Select a rectangle to view extracted data
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Export a simple connected component for testing
export const ExtractedDataViewerConnected: React.FC = () => {
  const { selectedDocumentId } = useSelector(
    (state: ReduxState) => state.TransactionManager
  );
  const { job } = useSelector((state: ReduxState) => state.JobData);
  // console.log({ selectedDocumentId });
  const { pdfURL } = useBankStatementPDF({
    selectedDocumentId,
  });

  //   const testPdfUrl =
  //     "https://raw.githubusercontent.com/mozilla/pdf.js/ba2edeae/web/compressed.tracemonkey-pldi-09.pdf";
  return (
    <ExtractedDataViewer
      imageSrc={pdfURL}
      documentId={selectedDocumentId}
      jobId={job?.id}
    />
  );
};

export default ExtractedDataViewerConnected;
